import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaShippingFast,
  FaCreditCard,
  FaHeadset,
  FaSmile,
} from "react-icons/fa";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import axios from "axios";
import { SERVER_URL } from "../../../constants.js";
import { useCategoryContext } from "../../../context/CategoryContext.js";

const Dcategory = ({name}) => {
  const { categories, loading } = useCategoryContext();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const SkeletonCard = () => (
    <div className="relative group w-60 flex-shrink-0 rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105 animate-pulse">
      <div className="w-full h-40 bg-gray-300"></div>
      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
        <div className="w-24 h-6 bg-gray-400"></div>
      </div>
      <div className="mt-2 text-center">
        <div className="w-24 h-6 bg-gray-400 mx-auto"></div>
      </div>
    </div>
  );

  const fetchProductsByCategory = async (categoryName) => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${SERVER_URL}/api/v1/product`);
      const { data } = response.data;
      const filtered = data.filter((product) => product.category === name);
      setFilteredProducts(filtered);
      setSelectedCategory(categoryName);
    } catch (err) {
      console.error("Error fetching products by category:", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };

  const handleViewAll = () => {
    navigate("/categories");
  };

  return (
    <div className="container mx-auto py-8">
      {/* Services Section */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mx-4 mb-12">
        {[
          {
            icon: <FaShippingFast />,
            title: "Free Delivery",
            description: "For all orders over Rs 120",
          },
          {
            icon: <FaCreditCard />,
            title: "Safe Payment",
            description: "100% secure payment",
          },
          {
            icon: <FaHeadset />,
            title: "24/7 Help Center",
            description: "Dedicated 24/7 support",
          },
          {
            icon: <FaSmile />,
            title: "Friendly Services",
            description: "30-day satisfaction guarantee",
          },
        ].map((item, index) => (
          <div
            key={index}
            className="flex flex-col items-center p-8 group rounded-2xl border-2 border-gray-100 hover:border-blue-500 shadow-sm hover:shadow-xl transition-all duration-300 bg-white"
          >
            <div className="p-6 rounded-full bg-blue-50 group-hover:bg-blue-500 transition-colors duration-300">
              <span className="text-4xl text-blue-500 group-hover:text-white transition-colors duration-300">
                {item.icon}
              </span>
            </div>
            <h4 className="font-bold mt-6 text-lg text-gray-800">{item.title}</h4>
            <p className="text-gray-600 mt-2 text-sm text-center">{item.description}</p>
          </div>
        ))}
      </div>
  
      {/* Categories Section */}
      <div className="py-8">
        <div className="flex justify-between items-center mb-8 px-4">
          <h2 className="text-2xl font-bold text-gray-800">Our Top Categories</h2>
          <button
            onClick={handleViewAll}
            className="px-6 py-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 font-semibold transition-colors duration-300"
          >
            View All
          </button>
        </div>
  
        <div className="relative px-4">
          <div
            className="flex overflow-x-auto no-scrollbar py-4 space-x-6"
            ref={scrollRef}
          >
            {loading
              ? Array.from({ length: 10 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))
              : categories.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => fetchProductsByCategory(item.name)}
                    className="relative group w-64 flex-shrink-0 rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105"
                  >
                    <img
                      src={item.imgUrl}
                      alt={item.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                      <h3 className="text-white text-xl font-semibold">
                        {item.name}
                      </h3>
                    </div>
                    <div className="absolute bottom-0 w-full p-4 bg-white/80 backdrop-blur-sm">
                      <h4 className="text-lg font-medium text-gray-800">
                        {item.name}
                      </h4>
                    </div>
                  </button>
                ))}
          </div>
  
          <button
            onClick={scrollLeft}
            className="absolute -left-2 top-1/2 transform -translate-y-1/2 bg-white hover:bg-blue-500 hover:text-white rounded-full p-3 text-blue-500 shadow-lg transition-all duration-300"
          >
            <IoIosArrowBack size={24} />
          </button>
          <button
            onClick={scrollRight}
            className="absolute -right-2 top-1/2 transform -translate-y-1/2 bg-white hover:bg-blue-500 hover:text-white rounded-full p-3 text-blue-500 shadow-lg transition-all duration-300"
          >
            <IoIosArrowForward size={24} />
          </button>
        </div>
      </div>
  
      {/* Products Section */}
      {selectedCategory && (
        <div className="py-12">
          <h2 className="text-2xl font-bold mb-8 px-4 text-gray-800">
            {selectedCategory} Products
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 px-4">
            {loadingProducts
              ? Array.from({ length: 8 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))
              : filteredProducts.map((product) => (
                  <div
                    key={product._id}
                    className="group rounded-2xl shadow-lg overflow-hidden bg-white hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={product.imgUrl[0]}
                        alt={product.name}
                        className="w-full h-48 object-cover transform group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        {product.name}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                        {product.description}
                      </p>
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-blue-500 font-bold text-lg">
                            Rs {product.price}
                          </div>
                          <div className="line-through text-gray-400 text-sm">
                            Rs {product.originalPrice}
                          </div>
                        </div>
                        <button 
                          onClick={() => navigate(`/product/${product._id}`)}
                          className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors duration-300 transform hover:scale-105"
                        >
                          View Product
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dcategory;
